import React, { useState } from "react";
import {
  // BarChart3,
  Building2,
  ChevronDown,
  ClipboardList,
  Clock3,
  Cpu,
  Factory,
  FileText,
  GitBranch,
  LayoutGrid,
  ListOrdered,
  Link2,
  LogOut,
  Package,
  ScanQrCode,
  Settings as SettingsIcon,
  ShieldCheck,
  Truck,
  User,
  UserCircle,
  Workflow,
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetDataApiCache } from "../../app/apiCache";
import { clearAuthSession, notifyAuthSessionChanged } from "../../services/authSession";

// Top-level items match the wireframe (Analytics / Real Time Analytics /
// Batch List / Reports / Settings), plus Dispatch History surfaced as its
// own top-level item per the explicit requirement that it stay directly
// reachable rather than folded into Settings.
const topLevelItems = [
  { to: "/setting/analytics", label: "Analytics", icon: UserCircle },
  { to: "/setting/real-time-analytics", label: "Real Time Analytics", icon: LayoutGrid },
  { to: "/setting/batch", label: "Batch List", icon: ClipboardList },
  { to: "/setting/dispatch-history", label: "Dispatch History", icon: Truck },
  { to: "/setting/oem", label: "Customer", icon: Factory },
  { to: "/setting/raw-material", label: "Raw Material", icon: Package },
  { to: "/setting/input-batch", label: "Input Batch", icon: ClipboardList },
  // { to: "/setting/reports", label: "Reports", icon: BarChart3 },
  { to: "/setting/scanner", label: "QR Scanner", icon: ScanQrCode },
];

// Everything that used to be flat top-level master-data links now lives
// behind the wireframe's collapsible "Settings" group (chevron) — routes
// are unchanged, only the navigation grouping is.
const settingsItems = [
  { to: "/setting/plant", label: "Plant", icon: Building2 },
  { to: "/setting/shift", label: "Shift", icon: Clock3 },
  { to: "/setting/line", label: "Line", icon: GitBranch },
  { to: "/setting/relation-type", label: "Relation Type", icon: Link2 },
  { to: "/setting/operation", label: "Operation", icon: Workflow },
  { to: "/setting/machine", label: "Machine", icon: Cpu },
  { to: "/setting/item", label: "Item", icon: Package },
  { to: "/setting/item-routing", label: "Item Routing", icon: ListOrdered },
  { to: "/setting/qr-verification", label: "QR Verify", icon: ShieldCheck },
];

const userManagementItems = [
  { to: "/setting/page", label: "Page", icon: FileText },
  { to: "/setting/role", label: "Role", icon: ShieldCheck },
  { to: "/setting/user", label: "User", icon: User },
];

const getLinkClassName = ({ isActive }) =>
  [
    "group relative flex items-center gap-3 rounded-lg p-2 text-slate-600 transition-colors",
    isActive
      ? "bg-lightSecondary text-brand shadow-sm"
      : "hover:bg-white/75 hover:text-brand",
  ].join(" ");

const NavItem = ({ to, label, icon: Icon, expanded, onClick }) => (
  <NavLink to={to} className={getLinkClassName} onClick={onClick}>
    <Icon className="h-5 w-5 shrink-0" />
    <span className={expanded ? "inline" : "inline md:hidden"}>{label}</span>
    {!expanded && (
      <span className="absolute left-full top-1/2 z-[90] ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg group-hover:opacity-100 md:block">
        {label}
      </span>
    )}
  </NavLink>
);

/** Collapsible group with a chevron, matching the wireframe's "Settings >" affordance. */
const CollapsibleGroup = ({ label, icon: Icon, items, expanded, isActive, onNavigate }) => {
  const [open, setOpen] = useState(isActive);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={[
          "flex w-full items-center gap-3 rounded-lg p-2 text-slate-600 transition-colors hover:bg-white/75 hover:text-brand",
          isActive && "text-brand",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <Icon className="h-5 w-5 shrink-0" />
        <span className={`flex-1 text-left ${expanded ? "inline" : "inline md:hidden"}`}>{label}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""} ${
            expanded ? "inline" : "inline md:hidden"
          }`}
        />
      </button>
      {open && (expanded || window.innerWidth < 768) && (
        <div className="mt-1 space-y-1 border-l border-slate-100 pl-3">
          {items.map((item) => (
            <NavItem key={item.to} {...item} expanded={expanded} onClick={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ expanded, isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLinkClick = () => {
    if (window.innerWidth < 768) setIsOpen(false);
  };

  const handleLogout = () => {
    resetDataApiCache(dispatch);
    clearAuthSession();
    notifyAuthSessionChanged();
    navigate("/", { replace: true });
  };

  const isSettingsActive = settingsItems.some((item) => location.pathname.startsWith(item.to));
  const isUserManagementActive = userManagementItems.some((item) => location.pathname.startsWith(item.to));

  return (
    <>
      {isOpen && (
        <button
          type="button"
          className="fixed inset-0 z-50 bg-black/40 md:hidden"
          onClick={() => setIsOpen(false)}
          aria-label="Close sidebar"
        />
      )}

      <aside
        className={`
          fixed left-0 top-0 z-[70] flex h-screen max-w-[86vw] flex-col overflow-y-auto overflow-x-visible border-r border-sky-100 bg-white
          transition-all duration-300 md:relative md:left-auto md:top-auto md:h-auto
          md:min-h-[calc(100vh-64px)] md:self-stretch
          ${expanded ? "w-64" : "w-72 md:w-20"}
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
      >
        <nav className="flex h-full flex-1 flex-col gap-1 p-3">
          {topLevelItems.map((item) => (
            <NavItem key={item.to} {...item} expanded={expanded} onClick={handleLinkClick} />
          ))}

          <CollapsibleGroup
            label="Settings"
            icon={SettingsIcon}
            items={settingsItems}
            expanded={expanded}
            isActive={isSettingsActive}
            onNavigate={handleLinkClick}
          />

          <div className="flex-1" />

          <div className="space-y-1 border-t border-slate-100 pt-3">
            <CollapsibleGroup
              label="User Management"
              icon={User}
              items={userManagementItems}
              expanded={expanded}
              isActive={isUserManagementActive}
              onNavigate={handleLinkClick}
            />

            <button
              type="button"
              onClick={handleLogout}
              className="group relative flex w-full items-center gap-3 rounded-lg p-2 text-rose-500 transition-colors hover:bg-rose-50"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span className={expanded ? "inline" : "inline md:hidden"}>Logout</span>
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
